import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, HashRouter, Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { meaning, redirectOf, t } from '@leo/model';
import './styles.css';
import { Group } from './pages/areas/Group';
import { Overview } from './pages/settings/Overview';
import { Issues } from './pages/settings/Issues';
import { Families } from './pages/settings/Families';
import { Audit } from './pages/settings/Audit';
import { Organisation } from './pages/settings/Organisation';
import { MarginSettings } from './pages/settings/MarginSettings';
import { DataHub } from './pages/settings/DataHub';
import { Sales } from './pages/sales/Sales';
import { EntityPage } from './pages/sales/Entity';
import { StockModule } from './pages/stock/Stock';
import { Decisions } from './pages/areas/Decisions';
import { Plan } from './pages/areas/Plan';
import { MoneyModule } from './pages/money/Money';
import { BuyingModule } from './pages/buying/Buying';
import { MarketsModule } from './pages/markets/Markets';
import { api, devIdentities, getToken, setToken } from './api';
import { ChangePassword, Login, Setup } from './pages/auth/Login';
import { People } from './pages/settings/People';
import { FreshChip, SystemPage, type Fresh } from './pages/settings/System';
import { Connections } from './pages/settings/Connections';
import { DealerGroupsNotMatched } from './pages/settings/DealerGroupsNotMatched';
import { Checks } from './pages/settings/Checks';
import { DataHealth, HealthStrip, useHealthBanner } from './pages/health/DataHealth';
import { AskBar, AskProvider } from './ui/AskBox';
import { ThemeSwitch, applyTheme, readTheme } from './ui/theme';
import { SearchBox } from './ui/search';
import { loadValueBasis, useValueBasis } from './ui/basis';
import { AREAS, BottomNav, Ico, SETTINGS } from './ui/nav';
import { carried, useCarried } from './ui/scope';
import { useUpdating } from './components';

/** The signed-in person's permissions (null while unknown: pages then rely on the API's own answers). */
const Perms = React.createContext<string[] | null>(null);
/** A screen whose figures need a permission the person does not hold says so, instead of a page of refusals. */
function Gate({ perm, children }: { perm: string; children: React.ReactNode }) {
  const perms = React.useContext(Perms);
  if (perms && !perms.includes(perm)) return <div className="page"><div className="empty"><b>Not available for your role</b>This screen shows business figures your role does not include. <NavLink to="/settings/data-loads">Go to Settings</NavLink></div></div>;
  return <>{children}</>;
}

const SNAPSHOT = typeof window !== 'undefined' && !!(window as any).__LEO_SNAPSHOT__;
if (typeof document !== 'undefined') applyTheme(readTheme());

/** A screen that throws shows a message and a retry — never a blank app. */
class Boundary extends React.Component<{ children: React.ReactNode }, { err: string | null }> {
  state = { err: null as string | null };
  static getDerivedStateFromError(e: Error) { return { err: e.message }; }
  render() { return this.state.err ? <div className="page"><div className="callout"><b>This screen could not be drawn.</b> {this.state.err} <button className="link" onClick={() => this.setState({ err: null })}>Try again</button></div></div> : this.props.children; }
}
const Router: any = SNAPSHOT ? HashRouter : BrowserRouter;

function Shell({ children }: { children: React.ReactNode }) {
  const loc = useLocation(); useCarried();
  const inSettings = loc.pathname.startsWith('/settings');
  const screen = loc.pathname.split('/')[1] || 'today';
  const [tok, setTok] = React.useState(getToken());
  const [me, setMe] = React.useState<{ name: string; roleLabel: string; permissions: string[] } | null>(null); const [open, setOpen] = React.useState(false);
  React.useEffect(() => { api<{ name: string; roleLabel: string; permissions: string[] }>('/api/v1/auth/me').then(setMe).catch(() => setMe(null)); }, [tok]);
  // the value basis (Values: MRP / net incl. GST / NRV): the person's own setting follows them; a change redraws the screen
  React.useEffect(() => { if (!SNAPSHOT) void loadValueBasis(); }, [tok]); const basis = useValueBasis();
  const [meta, setMeta] = React.useState<{ demo: boolean; asOf: string | null; fresh?: Fresh } | null>(null);
  React.useEffect(() => { api<{ demo: boolean; asOf: string | null; fresh?: Fresh }>('/api/v1/meta').then(setMeta).catch(() => setMeta(null)); }, [tok]);
  const canUsers = !me || me.permissions.includes('users.manage');
  const mayOpen = (perm?: string) => !perm || !me || me.permissions.includes(perm);
  const banner = useHealthBanner(); const serious = (banner?.critical ?? 0) + (banner?.high ?? 0);
  const signOut = async () => { try { await api('/api/v1/auth/logout', { method: 'POST' }); } catch { /* already out */ } try { sessionStorage.removeItem('leo.dev.token'); } catch { /* ignore */ } location.assign('/login'); };
  return (
    <div className="shell">
      <aside className="nav">
        <div className="brand"><div className="mark">L</div><div><div className="name">LEO CORE</div><div className="sub">The intelligence behind every decision</div></div></div>
        <section>
          {AREAS.filter(a => mayOpen(a.perm)).map(a => <NavLink key={a.to} to={carried(a.to)} className={({ isActive }) => isActive ? 'active' : ''} title={meaning(a.key)}><Ico k={a.ico} /><span className="lbl">{t(a.key)}</span>{a.to === '/health' && serious > 0 ? <span className={`navcount ${banner?.critical ? 'bad' : ''}`} title={`${banner?.critical ?? 0} figures may be wrong, ${banner?.high ?? 0} problems to fix soon`}>{serious}</span> : null}</NavLink>)}
        </section>
        <section className="settings">
          <NavLink to="/settings/data-loads" className={inSettings ? 'active' : ''} title={meaning('nav.settings')}><Ico k="settings" /><span className="lbl">{t('nav.settings')}</span></NavLink>
          {inSettings && SETTINGS.filter(s => s.key !== 'people' || canUsers).map(s => <NavLink key={s.to} to={s.to} className={({ isActive }) => `sub ${isActive ? 'active' : ''}`} title={meaning(s.key)}><span className="dot" /><span className="lbl">{t(s.key)}</span></NavLink>)}
        </section>
        <div className="foot">Every figure carries its source and as-of date.</div>
      </aside>
      <div className="main">
        {meta?.demo && <div className="demobar"><b>Demo workspace</b> — every figure on these screens is generated to show how LEO CORE works. None of it is Leo Group's data.</div>}
        {SNAPSHOT && <div className="callout" style={{ margin: '12px 24px 0' }}><b>Preview snapshot</b> — {meta?.demo ? 'generated demo figures' : 'real data'} as of {(window as any).__LEO_SNAPSHOT__.takenAt}, frozen. Approvals and uploads here are not saved; the live app is at core.leogroupin.com.</div>}
        <div className="topbar">
          {mayOpen('command.view') && <AskBar screen={screen} />}
          <SearchBox />
          {!SNAPSHOT && <FreshChip fresh={meta?.fresh} />}
          <ThemeSwitch />
          <div className="who">
            {SNAPSHOT ? <span className="chip-user" title="Preview snapshot">Preview · Owner</span> : (
              <div className="menu">
                <button className="chip-user" onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open}><b>{me?.name ?? '…'}</b><span className="muted small">{me?.roleLabel ?? ''}</span></button>
                {open && (
                  <div className="pop" role="menu" onMouseLeave={() => setOpen(false)}>
                    <a href="/password">Change password</a>
                    {canUsers && <NavLink to="/settings/people" onClick={() => setOpen(false)}>{t('people')}</NavLink>}
                    {import.meta.env.DEV && <select aria-label="Switch development identity" value={tok} onChange={e => { setToken(e.target.value); setTok(e.target.value); location.reload(); }}><option value="">Session (signed in)</option>{devIdentities.map(d => <option key={d.token} value={d.token}>{d.label} (dev token)</option>)}</select>}
                    <button onClick={signOut}>Sign out</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <UpdatingBar />
        <HealthStrip banner={banner} />
        <main key={`${loc.pathname}|${basis}`}><Perms.Provider value={me?.permissions ?? null}><Boundary>{children}</Boundary></Perms.Provider></main>
        <BottomNav mayOpen={mayOpen} canUsers={canUsers} inSettings={inSettings} />
      </div>
    </div>
  );
}

/** "Updating…" while the screen's figures are being read again (after a filter changes); the old figures are not shown meanwhile. */
function UpdatingBar() { const on = useUpdating(); return <div className={`updating${on ? ' on' : ''}`} role="status" aria-live="polite">{on ? 'Updating…' : ''}</div>; }

/** Any old route is translated to its new home, keeping the rest of the path and the query. */
function LegacyRedirect() { const loc = useLocation(); const to = redirectOf(loc.pathname + loc.search); return to !== loc.pathname + loc.search ? <Navigate to={to} replace /> : <div className="page"><div className="empty"><b>Not here</b>This page does not exist.</div></div>; }


const AUTH_PATHS = ['/login', '/setup', '/password'];
function App() {
  const loc = useLocation();
  if (AUTH_PATHS.includes(loc.pathname) && !SNAPSHOT) return <Routes><Route path="/login" element={<Login />} /><Route path="/setup" element={<Setup />} /><Route path="/password" element={<ChangePassword forced />} /></Routes>;
  return (
      <AskProvider>
        <Shell>
          <Routes>
            <Route path="/" element={<Navigate to="/group" replace />} />
            <Route path="/group" element={<Gate perm="command.view"><Group /></Gate>} />
            <Route path="/sales" element={<Gate perm="sales.view"><Sales /></Gate>} />
            <Route path="/sales/:kind/:id" element={<Gate perm="sales.view"><EntityPage /></Gate>} />
            <Route path="/stock/*" element={<Gate perm="inventory.view"><StockModule /></Gate>} />
            <Route path="/finance/*" element={<MoneyModule />} />
            <Route path="/buying/*" element={<Gate perm="inventory.view"><BuyingModule /></Gate>} />
            <Route path="/markets/*" element={<Gate perm="sales.view"><MarketsModule /></Gate>} />
            <Route path="/plan" element={<Gate perm="plan.view"><Plan /></Gate>} />
            <Route path="/decisions" element={<Gate perm="decisions.view"><Decisions /></Gate>} />
            <Route path="/health" element={<DataHealth />} />
            <Route path="/settings" element={<Navigate to="/settings/data-loads" replace />} />
            <Route path="/settings/data-loads" element={<DataHub />} />
            <Route path="/settings/data-quality" element={<Overview />} />
            <Route path="/settings/checks" element={<Checks />} />
            <Route path="/settings/data-quality/findings" element={<Issues />} />
            <Route path="/settings/data-quality/findings/:id" element={<Issues />} />
            <Route path="/settings/mappings" element={<Families />} />
            <Route path="/settings/mappings/:leoId" element={<Families />} />
            <Route path="/settings/brand-terms" element={<MarginSettings />} />
            <Route path="/settings/group-structure" element={<Organisation />} />
            <Route path="/settings/connections" element={<Connections />} />
            <Route path="/settings/dealer-groups/not-matched" element={<Gate perm="mdm.decide"><DealerGroupsNotMatched /></Gate>} />
            <Route path="/settings/people" element={<People />} />
            <Route path="/settings/audit" element={<Audit />} />
            <Route path="/settings/system" element={<SystemPage />} />
            <Route path="*" element={<LegacyRedirect />} />
          </Routes>
        </Shell>
      </AskProvider>
  );
}
createRoot(document.getElementById('root')!).render(<React.StrictMode><Router><App /></Router></React.StrictMode>);
